/**
 * @fileoverview Instructor-facing pending organization invitations (Spec 11).
 * Accepting joins the org — the opt-in that authorizes org visibility into the
 * instructor's students. Renders nothing when there are no invites.
 *
 * It answers in two places, because an org invitation is an invitation before
 * it is anything else: inside the Programs page's Invites tab, alongside the
 * program invitations, and on Settings. `onCount` lets the host count them
 * without fetching the list twice — the Invites tab puts the number on the tab.
 */

import { useCallback, useContext, useEffect, useState } from "react";
import { Building2, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { getErrorMessage } from "@/lib/api";
import { AuthContext } from "@/App";

export default function OrgInviteBanner({ onCount, onChanged }) {
  const { setUser } = useContext(AuthContext) || {};
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/auth/org-invites");
      setInvites(res.data.invites || []);
    } catch (_) { /* non-critical */ }
  }, []);

  // Reported on every change, including the drop to zero: the tab's digit has
  // to disappear when the last invitation is answered, not on the next load.
  useEffect(() => { onCount?.(invites.length); }, [invites, onCount]);

  useEffect(() => { load(); }, [load]);

  const respond = async (token, action) => {
    setBusy(true);
    try {
      const res = await api.post(`/auth/org-invites/${token}/${action}`);
      if (action === "accept") {
        if (res.data?.user_id && setUser) setUser(res.data);
        toast.success("You've joined the organization");
      } else {
        toast.success("Invitation declined");
      }
      setInvites((prev) => prev.filter((i) => i.token !== token));
      onChanged?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't respond to the invitation"));
    } finally {
      setBusy(false);
    }
  };

  if (invites.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="org-invite-banner">
      {invites.map((inv) => (
        <div
          key={inv.token}
          className="flex flex-col gap-3 rounded-2xl border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] p-4 md:flex-row md:items-center md:justify-between"
        >
          <div className="flex items-center gap-3">
            <Building2 className="h-5 w-5 shrink-0 text-[var(--accent-yellow-text)]" />
            <p className="font-sans text-[15px] text-[var(--text-primary)]">
              <span className="font-semibold">{inv.org_name}</span> invited you to join their organization on Hans.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => respond(inv.token, "accept")}
              data-testid="org-invite-accept"
              className="inline-flex items-center gap-1.5 rounded-full bg-[var(--accent-yellow)] px-4 py-2 font-sans text-[14px] font-semibold text-[var(--text-inverse)] disabled:opacity-50"
            >
              <Check className="h-4 w-4" /> Accept
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => respond(inv.token, "decline")}
              className="inline-flex items-center gap-1.5 rounded-full border border-[var(--studio-divider-strong)] px-4 py-2 font-sans text-[14px] font-medium text-[var(--text-secondary)] disabled:opacity-50"
            >
              <X className="h-4 w-4" /> Decline
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
